import {
  AccountDetailForSelector,
  type PayOverTimeManualPaymentDetailResult,
  PayOverTimeManualPaymentDetailResultTypenames,
} from '@varo/graphql-schema-types';
import {
  buildErrorModel,
  type ErrorModel,
  logPayOverTimeError,
  resolveErrorCopy,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import { useEffect, useState } from 'react';
import FetchPayOverTimeManualPaymentDetails from 'services/graphql/queries/FetchPayOverTimeManualPaymentDetails';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { formatDate } from 'shared/utils/string/date';
import { useQuery } from 'urql';

const DETAILS_OPERATION = 'payOverTimeManualPaymentDetails';

export interface UsePayOverTimeManualPaymentDetailsResult {
  paymentAccounts: AccountDetailForSelector[];
  loanPaymentAmount: string;
  setLoanPaymentAmount: (amount: string) => void;
  dueDate: string;
  loanId: string;
  amountDueNumber: number;
  outstandingBalanceNumber: number;
  errorModel: ErrorModel | null;
  loading: boolean;
}

const usePayOverTimeManualPaymentDetails = (
  loanId: string,
): UsePayOverTimeManualPaymentDetailsResult => {
  const [{ data, error, fetching }] = useQuery<{
    payOverTimeManualPaymentDetails: PayOverTimeManualPaymentDetailResult;
  }>({
    query: FetchPayOverTimeManualPaymentDetails,
    variables: { loanId },
    requestPolicy: 'cache-and-network',
  });
  const [enteredAmount, setEnteredAmount] = useState<string | null>(null);

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const networkErrorModel = error
    ? buildErrorModel(defaultTitle, defaultMessage)
    : null;

  const paymentDetailsData =
    data?.payOverTimeManualPaymentDetails?.__typename ===
    PayOverTimeManualPaymentDetailResultTypenames.PayOverTimeManualPaymentDetailSuccess
      ? data.payOverTimeManualPaymentDetails
      : null;

  const paymentDetailsError =
    data?.payOverTimeManualPaymentDetails?.__typename ===
    PayOverTimeManualPaymentDetailResultTypenames.PayOverTimeManualPaymentDetailError
      ? data.payOverTimeManualPaymentDetails
      : null;

  const paymentDetailsErrorModel = paymentDetailsError
    ? buildErrorModel(
        resolveErrorCopy(paymentDetailsError.title, defaultTitle),
        resolveErrorCopy(paymentDetailsError.message, defaultMessage),
      )
    : null;

  useEffect(() => {
    if (!error) {
      return;
    }

    logPayOverTimeError(error, { loanId, operation: DETAILS_OPERATION });
  }, [error, loanId]);

  useEffect(() => {
    if (!paymentDetailsError) {
      return;
    }

    logPayOverTimeError(new Error(paymentDetailsError.message), {
      loanId,
      code: paymentDetailsError.code,
      typename: paymentDetailsError.__typename,
      operation: DETAILS_OPERATION,
    });
  }, [paymentDetailsError, loanId]);

  const paymentDetailsView = paymentDetailsData?.payOverTimeLoanPaymentView;

  const combinedError = networkErrorModel ?? paymentDetailsErrorModel;

  const prepopulatedAmount =
    paymentDetailsView?.prepopulatedPaymentAmount?.amount;

  return {
    paymentAccounts: paymentDetailsData?.fromAccountSelectorView ?? [],
    loanPaymentAmount: enteredAmount ?? prepopulatedAmount?.toString() ?? '',
    setLoanPaymentAmount: setEnteredAmount,
    dueDate: paymentDetailsView?.dueDate
      ? formatDate(paymentDetailsView.dueDate, 'MMMM d, yyyy')
      : '',
    amountDueNumber: Number(prepopulatedAmount ?? 0),
    outstandingBalanceNumber: Number(
      paymentDetailsView?.outstandingBalance?.amount ?? 0,
    ),
    loanId: paymentDetailsView?.loanId ?? '',
    errorModel: combinedError,
    loading: fetching,
  };
};

export default usePayOverTimeManualPaymentDetails;
